// Pricing Plans Configuration
// ⚠️ WARNING: This is client-side configuration only!
// All pricing enforcement MUST be done server-side to prevent tampering.
// This file is for UI display purposes only.

export interface PricingPlan {
  id: string;
  name: string;
  price: number; // in USD per month
  currency: string;
  interval: 'month' | 'year';
  description: string;
  features: PlanFeature[];
  limits: PlanLimits;
  popular?: boolean;
  badge?: string;
}

export interface PlanFeature {
  text: string;
  included: boolean;
  tooltip?: string;
}

export interface PlanLimits {
  requests: number | 'unlimited'; // -1 for unlimited
  dbConnections: number | 'unlimited';
  tokens: number | null; // null for not applicable
  models: string[];
}

export const PRICING_PLANS: PricingPlan[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    currency: 'USD',
    interval: 'month',
    description: 'Perfect for trying out Dytafly',
    features: [
      { text: '20 requests per month', included: true },
      { text: 'Up to 3 database connections', included: true },
      { text: 'GPT-3.5 model access', included: true },
      { text: 'Community support', included: true },
      { text: 'Basic query templates', included: true },
      { text: 'Token limits', included: false },
      { text: 'Priority support', included: false },
      { text: 'Advanced models', included: false },
    ],
    limits: {
      requests: 20,
      dbConnections: 3,
      tokens: null,
      models: ['gpt-3.5-turbo'],
    },
  },
  {
    id: 'starter',
    name: 'Starter',
    price: 15,
    currency: 'USD',
    interval: 'month',
    description: 'Great for individuals and small teams',
    popular: true,
    badge: 'Most Popular',
    features: [
      { text: 'Unlimited requests', included: true },
      { text: 'Unlimited database connections', included: true },
      { text: 'GPT-4 & GPT-4-Turbo access', included: true },
      { text: '15 Million tokens per month', included: true },
      { text: 'Email support', included: true },
      { text: 'All query templates', included: true },
      { text: 'Export functionality', included: true },
      { text: 'Advanced models (GPT-4-Vision, Claude)', included: false },
    ],
    limits: {
      requests: 'unlimited',
      dbConnections: 'unlimited',
      tokens: 15000000,
      models: ['gpt-4', 'gpt-4-turbo-preview'],
    },
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 25,
    currency: 'USD',
    interval: 'month',
    description: 'For power users and growing businesses',
    features: [
      { text: 'Unlimited requests', included: true },
      { text: 'Unlimited database connections', included: true },
      { text: 'All AI models (GPT-4, GPT-4-Turbo, GPT-4-Vision)', included: true },
      { text: '30 Million tokens per month', included: true },
      { text: 'Priority support', included: true },
      { text: 'All query templates', included: true },
      { text: 'Advanced export options', included: true },
      { text: 'Custom API access (coming soon)', included: true, tooltip: 'Use Dytafly with your own API keys' },
      { text: 'Team collaboration features', included: true },
    ],
    limits: {
      requests: 'unlimited',
      dbConnections: 'unlimited',
      tokens: 30000000,
      models: ['gpt-4', 'gpt-4-turbo-preview', 'gpt-4-vision-preview', 'gpt-3.5-turbo-16k'],
    },
  },
];

// Helper functions
export const getPlanById = (planId: string): PricingPlan | undefined => {
  return PRICING_PLANS.find(plan => plan.id === planId);
};

/**
 * @deprecated Use useSubscription() hook from SubscriptionContext instead
 *
 * This function relied on client-side localStorage which:
 * 1. Is insecure (users can manually modify it)
 * 2. Gets stale after payment (not updated)
 * 3. Doesn't reflect actual server state
 *
 * Migration: Import and use useSubscription() hook to get real subscription data
 * ```typescript
 * const { subscription } = useSubscription();
 * const currentPlan = subscription?.plan_id || 'free';
 * ```
 */
export const getCurrentPlan = (): string => {
  console.warn(
    'getCurrentPlan() is deprecated. Use useSubscription() hook instead.',
    'See config/pricing.ts for migration guide.'
  );
  return localStorage.getItem('currentPlan') || 'free';
};

export const isPlanFeatureAvailable = (feature: string, planId?: string): boolean => {
  const currentPlanId = planId || getCurrentPlan();
  const plan = getPlanById(currentPlanId);

  if (!plan) return false;

  // Check specific feature availability based on plan
  switch (feature) {
    case 'unlimited_requests':
      return plan.limits.requests === 'unlimited';
    case 'unlimited_connections':
      return plan.limits.dbConnections === 'unlimited';
    case 'advanced_models':
      return plan.limits.models.some(m => m.includes('gpt-5') || m.includes('o3'));
    default:
      return false;
  }
};

export const checkRequestLimit = (currentRequests: number, planId?: string): boolean => {
  // ⚠️ WARNING: This is client-side validation only!
  // Server MUST enforce actual limits to prevent abuse
  // This is for UI feedback only - actual enforcement via API middleware
  const currentPlanId = planId || getCurrentPlan();
  const plan = getPlanById(currentPlanId);

  if (!plan) return false;

  if (plan.limits.requests === 'unlimited') return true;

  return currentRequests < plan.limits.requests;
};

export const checkTokenLimit = (currentTokens: number, planId?: string): boolean => {
  const currentPlanId = planId || getCurrentPlan();
  const plan = getPlanById(currentPlanId);

  if (!plan || !plan.limits.tokens) return true;

  return currentTokens < plan.limits.tokens;
};

export const getAvailableModels = (planId?: string): string[] => {
  const currentPlanId = planId || getCurrentPlan();
  const plan = getPlanById(currentPlanId);

  return plan?.limits.models || ['gpt-3.5-turbo'];
};